function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `sectionId{SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `element.id-{uniqueKey}`);
      element.form && element.setAttribute('form', `element.form.getAttribute('id')-{uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-this.dataset.productId-{this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`animationClassOut-{direction}`);
    nextSlide.classList.add(`animationClassIn-{direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`animationClassOut-{direction}`);
      nextSlide.classList.remove(`animationClassIn-{direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`this.dataset.url&productid={productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

(function () {
  /* ---------- helpers ---------- */
  function parseBool(v){ return String(v) === 'true'; }
  function parseIntSafe(v, def){ var n = parseInt(v); return isNaN(n) ? def : n; }
  function pxOrDefault(v, def){ return (v !== undefined && v !== '') ? (v + 'px') : def; }

  function getOptionsFromWrapper(wrapper) {
    return {
      type: wrapper.dataset.type || 'slide',
      perPage: parseIntSafe(wrapper.dataset.slidesDesktop, 1),
      perMove: 1,
      autoplay: parseBool(wrapper.dataset.autoplay),
      interval: (parseIntSafe(wrapper.dataset.autoplaySpeed, 5) || 5) * 1000,
      gap: pxOrDefault(wrapper.dataset.gapDesktop, '15px'),
      padding: {
        left: pxOrDefault(wrapper.dataset.sidePaddingDesktop, '15px'),
        right: pxOrDefault(wrapper.dataset.sidePaddingDesktop, '15px'),
      },
      breakpoints: {
        750: {
          perPage: parseIntSafe(wrapper.dataset.slidesMobile, 1),
          gap: pxOrDefault(wrapper.dataset.gapMobile, '15px'),
          padding: {
            left: pxOrDefault(wrapper.dataset.sidePaddingMobile, '15px'),
            right: pxOrDefault(wrapper.dataset.sidePaddingMobile, '15px'),
          }
        }
      },
      arrows: false,
      pagination: true,
      direction: 'ltr',
      drag: true
    };
  }

  /* ---------- init / recreate logic ---------- */
  function initSplideForWrapper(wrapper) {
    try {
      var splideEl = wrapper.querySelector('.splide');
      if (!splideEl) {
        console.warn('[splide] no .splide inside wrapper', wrapper);
        return;
      }

      var slidesCount = splideEl.querySelectorAll('.splide__slide').length;

      // If we already created an instance
      if (wrapper._splideInstance) {
        var prevCount = wrapper._slidesCount || 0;
        // If slide count changed, destroy and recreate (editor likely updated content)
        if (slidesCount !== prevCount) {
          try {
            wrapper._splideInstance.destroy();
          } catch (err) {
            console.warn('[splide] destroy error', err);
          }
          delete wrapper._splideInstance;
          wrapper._slidesCount = 0;
        } else {
          // otherwise try refresh to make sure Splide re-calcs
          try {
            wrapper._splideInstance.refresh();
            console.log('[splide] refreshed existing instance', wrapper);
            return;
          } catch (err) {
            console.warn('[splide] refresh failed, will recreate', err);
            try { wrapper._splideInstance.destroy(); } catch(e){}
            delete wrapper._splideInstance;
          }
        }
      }

      // create new instance
      var options = getOptionsFromWrapper(wrapper);
      var inst = new Splide(splideEl, options);

      // optional: ensure a small refresh after mount (helps editor)
      inst.on('mounted', function () {
        try { inst.refresh(); } catch (e) {}
      });

      inst.mount();
      wrapper._splideInstance = inst;
      wrapper._slidesCount = slidesCount;
      console.info('[splide] mounted', wrapper, 'slides=', slidesCount);
    } catch (err) {
      console.error('[splide] init error', err);
    }
  }

  function initAllSplides() {
    if (!window.Splide) {
      console.warn('[splide] Splide lib not loaded yet - skipping init');
      return;
    }
    document.querySelectorAll('splide-component').forEach(function (wrapper) {
      initSplideForWrapper(wrapper);
    });
  }

  /* ---------- wait for Splide if needed ---------- */
  function waitForSplideThenInit(timeoutMs) {
    timeoutMs = timeoutMs || 5000;
    if (window.Splide) { initAllSplides(); return; }
    var waited = 0;
    var t = setInterval(function () {
      if (window.Splide) {
        clearInterval(t);
        initAllSplides();
        return;
      }
      waited += 50;
      if (waited > timeoutMs) {
        clearInterval(t);
        console.warn('[splide] Splide not found after', timeoutMs, 'ms');
      }
    }, 50);
  }

  /* ---------- DOM ready ---------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { waitForSplideThenInit(5000); });
  } else {
    waitForSplideThenInit(5000);
  }

  /* ---------- listen for Shopify editor events (live preview) ---------- */
  window.addEventListener('shopify:section:load', function () { initAllSplides(); });
  window.addEventListener('shopify:section:select', function () { initAllSplides(); });
  window.addEventListener('shopify:block:select', function () { initAllSplides(); });
  window.addEventListener('shopify:block:deselect', function () { initAllSplides(); });

  /* ---------- MutationObserver fallback for dynamic updates ---------- */
  var debounceTimer = null;
  var observer = new MutationObserver(function (mutations) {
    var shouldRun = false;
    for (var m of mutations) {
      // if splide-component added/removed or attributes changed -> re-init
      if (m.type === 'childList') {
        for (var node of m.addedNodes) {
          if (node && node.matches && node.matches('splide-component')) { shouldRun = true; break; }
        }
        for (var node of m.removedNodes) {
          if (node && node.matches && node.matches('splide-component')) { shouldRun = true; break; }
        }
      }
      if (m.type === 'attributes' && (m.target && (m.target.matches && m.target.matches('splide-component') || m.target.closest && m.target.closest('splide-component')))) {
        shouldRun = true;
      }
      if (shouldRun) break;
    }
    if (shouldRun) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () { initAllSplides(); }, 80);
    }
  });

  try {
    observer.observe(document.documentElement || document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-autoplay','data-autoplay-speed','data-slides-desktop','data-slides-mobile','data-gap-desktop','data-gap-mobile','data-side-padding-desktop','data-side-padding-mobile'] });
  } catch (err) {
    console.warn('[splide] MutationObserver failed', err);
  }

  /* ---------- debug helper: expose manual re-init ---------- */
  window.__reinitSplide = function () { initAllSplides(); console.log('[splide] manual reinit called'); };

})();




// assets/bundle-variants.js
(() => {
  'use strict';

  // ---- Config ----
  const VARIANTS_JSON_SELECTOR = '[data-variants-json]';
  const BUNDLE_WRAPPER_SELECTOR = '.quantity-break__selector-item';
  const BUNDLE_SELECT_SELECTOR = '.quantity-break__variant-select';
  const ADD_BUNDLE_BUTTON_SELECTOR = '#AddToCartBundle';
  const LOG_PREFIX = '[BundleVariants]';

  let VARIANTS_DB = null;

  // Acceso a las traducciones originales de Shopify (Dawn/Reference themes)
  const variantStrings = window.variantStrings || {
    addToCart: "Agregar al carrito",
    soldOut: "Agotado",
    unavailable: "No disponible"
  };

  const safeText = (s) => (s === null || s === undefined) ? '' : String(s).replace(/\u00A0/g, ' ').trim().toLowerCase();

  // ---- 1. Lógica de Base de Datos ----
  function loadVariantsFromDOM() {
    const node = document.querySelector(VARIANTS_JSON_SELECTOR);
    if (!node) return;
    try {
      const parsed = JSON.parse(node.textContent);
      VARIANTS_DB = Array.isArray(parsed) ? parsed : (parsed.variants || null);
    } catch (e) { console.error(`${LOG_PREFIX} Error JSON:`, e); }
  }

  function matchVariant(selectedOptions) {
    if (!VARIANTS_DB) return null;
    const normSelected = selectedOptions.map(safeText);
    for (const v of VARIANTS_DB) {
      const vOptions = Array.isArray(v.options) ? v.options : [v.option1, v.option2, v.option3].filter(Boolean);
      if (vOptions.length !== normSelected.length) continue;
      const normVOptions = vOptions.map(safeText);
      if (normSelected.every((s, i) => s === normVOptions[i])) return v;
    }
    return null;
  }

  // ---- 2. FUNCIONES DE STOCK (Estilo Shopify Oficial) ----
  function updateOptionsAvailabilityUI(wrapper) {
    const selects = Array.from(wrapper.querySelectorAll(BUNDLE_SELECT_SELECTOR));
    selects.forEach((currentSelect, index) => {
      Array.from(currentSelect.options).forEach(option => {
        if (!option.value || option.value.includes('unavailable')) return;

        const simulatedSelection = selects.map((s, i) => i === index ? option.value : s.value);
        const matched = matchVariant(simulatedSelection);

        // Limpiamos texto previo usando el separador de Shopify
        let baseText = option.text.split(' - ')[0].split(' — ')[0];

        if (matched) {
          if (!matched.available) {
            option.text = `${baseText} — ${variantStrings.soldOut}`;
          } else {
            option.text = baseText;
          }
        } else {
          option.text = `${baseText} — ${variantStrings.unavailable}`;
        }
      });
    });
  }

  function validateBundleStock() {
    const activeRadio = document.querySelector('input[name="quantity"]:checked');
    if (!activeRadio) return;

    const activeContainer = activeRadio.closest('.quantity-break-wrapper');
    if (!activeContainer) return;
    
    const wrappers = activeContainer.querySelectorAll(BUNDLE_WRAPPER_SELECTOR);
    const submitBtn = document.querySelector(`${ADD_BUNDLE_BUTTON_SELECTOR}, form[action*="/cart/add"] [type="submit"]`);

    if (!submitBtn) return;

    const submitButtonText = submitBtn.querySelector('span:not(.sold-out-message):not(.loading__spinner)');
    const soldOutMessage = submitBtn.querySelector('.sold-out-message');

    let allAvailable = true;
    let anyUnavailable = false;

    wrappers.forEach(w => {
      const variantId = w.dataset.selectedVariantId;
      const variant = VARIANTS_DB?.find(v => v.id == variantId);
      if (!variant) anyUnavailable = true;
      if (variant && !variant.available) allAvailable = false;
    });

    // Mimetizamos la lógica de product-form.js
    if (!allAvailable || anyUnavailable) {
      submitBtn.setAttribute('disabled', 'disabled');
      submitBtn.setAttribute('aria-disabled', 'true');
      
      if (soldOutMessage) {
        if (submitButtonText) submitButtonText.classList.add('hidden');
        soldOutMessage.classList.remove('hidden');
      } else if (submitButtonText) {
        submitButtonText.textContent = anyUnavailable ? variantStrings.unavailable : variantStrings.soldOut;
      }
    } else {
      submitBtn.removeAttribute('disabled');
      submitBtn.removeAttribute('aria-disabled');
      
      if (soldOutMessage) {
        if (submitButtonText) submitButtonText.classList.remove('hidden');
        soldOutMessage.classList.add('hidden');
      }
      
      if (submitButtonText) {
        submitButtonText.textContent = "COMENZAR PEDIDO";
      }
    }
  }

  // ---- 3. Resolución de Variantes ----
  function resolveVariantForWrapper(wrapper) {
    if (!wrapper) return null;
    const selects = Array.from(wrapper.querySelectorAll(BUNDLE_SELECT_SELECTOR));
    const filtered = selects.map(s => s.value).filter(v => v && v !== 'products.product.value_unavailable');
    const matched = matchVariant(filtered);

    wrapper.dataset.selectedVariantId = matched ? matched.id : '';

    updateOptionsAvailabilityUI(wrapper);
    validateBundleStock();

    return matched;
  }

  // ---- 4. Recolección e Interceptación (Tus funciones originales intactas) ----
  function collectBundleItems() {
    const activeRadio = document.querySelector('input[name="quantity"]:checked');
    if (!activeRadio) return [];
    const activeContainer = activeRadio.closest('.quantity-break-wrapper');
    const wrappers = Array.from(activeContainer.querySelectorAll(BUNDLE_WRAPPER_SELECTOR));
    const items = [];
    wrappers.forEach(w => {
      const id = w.dataset.selectedVariantId || w.dataset.selectedId;
      if (id) items.push({ id: Number(id), quantity: 1 });
    });
    return items;
  }

  function attachListeners() {
    document.addEventListener('change', (e) => {
      if (e.target.matches(BUNDLE_SELECT_SELECTOR)) {
        const wrapper = e.target.closest(BUNDLE_WRAPPER_SELECTOR);
        if (wrapper) resolveVariantForWrapper(wrapper);
      }
      if (e.target.name === 'quantity') {
        validateBundleStock();
      }
    });
  }

  // Interceptor Submit (Mantiene tu lógica de Drawer/Notificación)
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form || (form.getAttribute('data-type') !== 'add-to-cart-form' && !form.classList.contains('product-form'))) return;
    const giftGroup = Array.from(document.querySelectorAll('[data-fg]')).find(node => node.dataset.form === form.getAttribute('id') && node.querySelector('[data-fg-card]'));
    if (giftGroup && window.FloopyGifts) return;
    if (document.querySelector('.quantity-break__selector-item') && !document.querySelector('quantity-breaks[data-floopy-variant-mode="true"]')) {
      e.preventDefault();
      e.stopImmediatePropagation();

      const items = collectBundleItems();
      if (items.length === 0) return;

      const submitButton = form.querySelector('[type="submit"]');
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      
      if (submitButton) {
        submitButton.setAttribute('aria-disabled', true);
        submitButton.classList.add('loading');
        const spinner = submitButton.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');
      }

      fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          items: items,
          sections: ['cart-drawer', 'cart-notification-product', 'cart-notification-button', 'cart-icon-bubble'],
          sections_url: window.location.pathname
        })
      })
      .then(res => res.json())
      .then(response => {
        if (response.status) throw response;
        
        // Actualización de UI (Tu lógica original de secciones)
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble && response.sections['cart-icon-bubble']) cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];

        if (cartDrawer && response.sections['cart-drawer']) {
          const drawerInner = cartDrawer.querySelector('.drawer__inner');
          const html = new DOMParser().parseFromString(response.sections['cart-drawer'], 'text/html');
          const liveContent = html.querySelector('.drawer__inner');
          if (liveContent && drawerInner) {
            drawerInner.innerHTML = liveContent.innerHTML;
            cartDrawer.classList.remove('is-empty');
          }
          setTimeout(() => {
            cartDrawer.classList.add('animate', 'active');
            document.body.classList.add('overflow-hidden');
          }, 50);
        } 
        else if (cartNotification && response.sections['cart-notification-product']) {
          cartNotification.renderContents({ key: response.items[0].key, sections: response.sections });
        } else {
          window.location.href = window.routes.cart_url;
        }
      })
      .catch(err => { console.error(err); window.location.href = window.routes.cart_url; })
      .finally(() => {
        if (submitButton) {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
          const spinner = submitButton.querySelector('.loading__spinner');
          if (spinner) spinner.classList.add('hidden');
        }
      });
    }
  }, true);

  function init() {
    loadVariantsFromDOM();
    attachListeners();
    document.querySelectorAll(BUNDLE_WRAPPER_SELECTOR).forEach(w => resolveVariantForWrapper(w));
  }

  init();
})();





document.addEventListener("DOMContentLoaded", () => {
  const bundles = document.querySelectorAll(".tpax-product-container");
  const hiddenInput = document.querySelector("#SelectedBundle");
  const productForm = document.querySelector("form[action*='/cart/add']");

  if (!bundles.length || !productForm) return;

  bundles.forEach(bundle => {
    bundle.addEventListener("click", () => {
      bundles.forEach(b => b.classList.remove("active"));
      bundle.classList.add("active");

      hiddenInput.value = `Bundle ${bundle.dataset.bundle} - ${bundle.dataset.price}`;
    });
  });

  productForm.addEventListener("submit", (e) => {
    if (!hiddenInput.value) {
      e.preventDefault();
      alert("Por favor selecciona un bundle antes de continuar.");
    }
  });
});




document.addEventListener("DOMContentLoaded", () => {
  const bundles = document.querySelectorAll(".tpax-product-container");
  const hiddenInput = document.querySelector("#SelectedBundle");
  const productForm = document.querySelector("form[action*='/cart/add']");

  if (!bundles.length || !productForm) return;

  bundles.forEach(bundle => {
    bundle.addEventListener("click", () => {
      // remover activo de todos
      bundles.forEach(b => b.classList.remove("active"));
      // marcar seleccionado
      bundle.classList.add("active");

      // actualizar input hidden con el bundle elegido
      hiddenInput.value = `Bundle ${bundle.dataset.bundle} - ${bundle.dataset.price}`;
    });
  });

  // asegurar que no se mande sin bundle
  productForm.addEventListener("submit", (e) => {
    if (!hiddenInput.value) {
      e.preventDefault();
      alert("Por favor selecciona un bundle antes de continuar.");
    }
  });
});


// Floopy: una variante Shopify por tarjeta del selector de cantidad.
(() => {
  'use strict';
  const selector = 'quantity-breaks[data-floopy-variant-mode="true"]';

  function init(root) {
    if (root.dataset.floopyInitialized === 'true') return;
    root.dataset.floopyInitialized = 'true';
    const form = document.getElementById(root.dataset.floopyProductFormId);
    if (!form) return;
    const variantField = form.querySelector('input[name="id"]');
    const radios = Array.from(root.querySelectorAll('input[type="radio"][name="quantity"][data-floopy-variant-id]'));
    const variantsNode = root.querySelector('script[type="application/json"]');
    let variants = [];
    try { variants = JSON.parse(variantsNode?.textContent || '[]'); } catch (error) { console.error('[Floopy] Variantes inválidas', error); }
    const button = form.querySelector('[type="submit"]');
    if (!variantField || !radios.length) return;

    function sync() {
      const selected = radios.find(r => r.checked) || radios[0];
      if (!selected.checked) selected.checked = true;
      const variant = variants.find(v => String(v.id) === selected.dataset.floopyVariantId);
      const available = Boolean(variant?.available);
      if (button) {
        button.disabled = !available;
        button.setAttribute('aria-disabled', String(!available));
      }
      if (!variant) {
        console.warn('[Floopy] Configurá un ID de variante válido para la opción elegida');
        return;
      }
      // Las tarjetas representan versiones, no cantidades de unidades.
      selected.value = '1';
      variantField.value = String(variant.id);
      variantField.setAttribute('value', String(variant.id));
      variantField.dispatchEvent(new Event('change', { bubbles: true }));
    }
    radios.forEach(radio => {
      radio.value = '1';
      radio.addEventListener('change', sync);
    });
    // Se sincroniza justo antes del envío para respetar la selección real.
    form.addEventListener('submit', event => {
      sync();
      const selected = radios.find(r => r.checked);
      const variant = variants.find(v => String(v.id) === selected?.dataset.floopyVariantId);
      if (!variant?.available) { event.preventDefault(); event.stopImmediatePropagation(); }
    }, true);
    sync();
  }
  function initAll() { document.querySelectorAll(selector).forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();


// Gifts v2 — eligibility follows the bundle option; Shopify enforces prices.
(() => {
  'use strict';
  if (window.FloopyGifts) return;
  const base = () => window.Shopify?.routes?.root || '/';
  const url = path => base() + path;
  const owned = item => item.properties?._floopy_gift === 'v1';
  let busy = false;
  let reconciling = false;
  let checkoutBusy = false;
  async function request(path, data) {
    const response = await fetch(url(path), data === undefined ? { cache: 'no-store' } : {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(data)
    });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.description || result.message || 'Shopify no pudo actualizar el carrito.');
    return result;
  }
  const cart = () => request('cart.js');
  function allowed(item, state) {
    const props = item.properties || {};
    if (props._floopy_options) {
      const options = String(props._floopy_options).split(',');
      const parents = state.items.filter(line => !owned(line) && line.quantity > 0 &&
        line.properties?._floopy_bundle_program === props._floopy_program &&
        options.includes(String(line.properties?._floopy_bundle_option)));
      // A partial pack no longer qualifies, even if the variant ID stayed the same.
      return parents.some(parent => {
        const p = parent.properties;
        const size = Number(p._floopy_bundle_size);
        const count = parents.filter(line => line.properties._floopy_bundle_option === p._floopy_bundle_option &&
          line.properties._floopy_bundle_size === p._floopy_bundle_size).reduce((sum, line) => sum + line.quantity, 0);
        return Number.isInteger(size) && size > 0 && count >= size;
      });
    }
    // Compatibility for gifts already present in the cart before this update.
    const ids = String(props._floopy_allowed || '').split(',').filter(Boolean);
    return state.items.some(line => !owned(line) && line.quantity > 0 && ids.includes(String(line.variant_id)));
  }
  // Never trust a "gift" property to set a price. Shopify must return an actual zero.
  async function reconcile(state) {
    let removedAny = false;
    for (let pass = 0; pass < 12; pass++) {
    const seen = new Set();
    const updates = {};
    let removed = false;
    for (const item of state.items) {
      if (!owned(item)) continue;
      const identity = `${item.properties._floopy_program}:${item.variant_id}`;
      if (!allowed(item, state) || item.final_line_price !== 0 || item.quantity !== 1 || seen.has(identity)) {
        updates[item.key] = 0;
        removed = true;
      } else seen.add(identity);
    }
    if (!removed) return { state, removed: removedAny };
    state = await request('cart/update.js', { updates });
    removedAny = true;
    }
    throw new Error('El carrito cambió demasiadas veces. Revisa los regalos antes de continuar.');
  }
  const accessOption = value => ({ '100': 1, '150': 2, option_1: 1, option_2: 2, option_3: 3, option_4: 4 }[value] || 0);
  function permitted(group, card, option) {
    const threshold = accessOption(card.dataset.access);
    return option > 0 && (card.dataset.access === 'both' || option === threshold ||
      (group.dataset.progressive === 'true' && threshold > 0 && option >= threshold));
  }
  function selected(group) {
    const form = document.getElementById(group.dataset.form);
    const selector = Array.from(document.querySelectorAll('quantity-breaks'))
      .find(node => node.dataset.floopyProductFormId === group.dataset.form);
    const radios = Array.from(selector?.querySelectorAll('input[type="radio"][data-fg-option]') || []);
    const radio = radios.find(node => node.checked);
    const option = Number(radio?.dataset.fgOption || 0);
    const variantMode = selector?.dataset.floopyVariantMode === 'true';
    const quantity = variantMode ? 1 : Number(radio?.dataset.fgQuantity || 0);
    let variants = [];
    try { variants = JSON.parse(selector?.querySelector('script[type="application/json"]')?.textContent || '[]'); } catch (_) {}
    if (!Array.isArray(variants)) variants = [];
    const id = variantMode ? radio?.dataset.floopyVariantId || '' : form?.querySelector('[name="id"]')?.value || '';
    return { form, selector, radio, option, quantity, variantMode, variants, id, radios };
  }
  function eligible(group, card, option) {
    return permitted(group, card, Number(option));
  }
  // Use the selected bundle's pickers, including packs with different variants per unit.
  function mainItems(selection, properties) {
    const { radio, variantMode, quantity, variants, id } = selection;
    if (!radio || !Number.isInteger(quantity) || quantity < 1) throw new Error('Selecciona un bundle disponible.');
    const wrappers = variantMode ? [] : Array.from(radio.closest('.quantity-break-wrapper')?.querySelectorAll('.quantity-break__selector-item') || []);
    const items = [];
    const add = (variantId, count) => {
      const variant = variants.find(v => String(v.id) === String(variantId));
      if (!variant?.available) throw new Error('Selecciona una variante disponible para cada unidad del bundle.');
      const existing = items.find(item => item.id === String(variant.id));
      if (existing) existing.quantity += count;
      else items.push({ id: String(variant.id), quantity: count, properties: { ...properties } });
    };
    if (wrappers.length) {
      if (wrappers.length !== quantity) throw new Error('Revisa los selectores de variantes del bundle.');
      for (const wrapper of wrappers) {
        const picks = Array.from(wrapper.querySelectorAll('.quantity-break__variant-select')).map(node => node.value);
        const normalize = value => String(value ?? '').replace(/\u00a0/g, ' ').trim().toLowerCase();
        const match = variants.find(v => {
          const options = v.options || [v.option1, v.option2, v.option3].filter(Boolean);
          return picks.length === options.length && picks.every((value, index) => normalize(value) === normalize(options[index]));
        });
        add(picks.length ? match?.id : wrapper.dataset.selectedVariantId || wrapper.dataset.selectedId, 1);
      }
    } else add(id, quantity);
    return items;
  }

  function paint(group) {
    const { option } = selected(group);
    group.querySelectorAll('[data-fg-card]').forEach(card => {
      const stock = card.dataset.available === 'true' && !!card.dataset.id;
      const open = eligible(group, card, option) && stock;
      card.dataset.locked = String(!open);
      card.querySelector('[data-fg-lock]').hidden = open;
      card.querySelector('[data-fg-locked]').hidden = open || !stock;
      card.querySelector('[data-fg-open]').hidden = true;
      card.querySelector('[data-fg-stock]').hidden = stock;
    });
  }
  function message(group, text) { group.querySelector('[data-fg-message]').textContent = text; }
  function announce(text) {
    let box = document.getElementById('floopy-cart-message');
    if (!box) {
      box = document.createElement('p'); box.id = 'floopy-cart-message'; box.setAttribute('role', 'status');
      box.style.cssText = 'padding:12px;margin:12px;border:1px solid currentColor;';
      (document.querySelector('main') || document.body).prepend(box);
    }
    box.textContent = text;
  }
  async function renderCart(state, bundledSections = null) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer?.renderContents && drawer.getSectionsToRender) {
      const ids = drawer.getSectionsToRender().map(section => section.section || section.id);
      let sections = bundledSections;
      if (!sections || ids.some(id => !sections[id])) {
        const response = await fetch(url('?sections=' + encodeURIComponent(ids.join(','))), { cache: 'no-store' });
        if (!response.ok) throw new Error('No se pudo actualizar la vista del carrito.');
        sections = await response.json();
      }
      if (ids.some(id => !sections[id])) throw new Error('Shopify no devolvió el carrito actualizado.');
      drawer.classList.toggle('is-empty', state.item_count === 0);
      drawer.renderContents({ ...state, sections });
    } else window.location.assign(url('cart'));
    document.dispatchEvent(new CustomEvent('floopy:gifts:updated', { detail: { cart: state } }));
  }
  async function add(group, form) {
    if (busy) return;
    busy = true;
    const buttons = Array.from(form.querySelectorAll('[type="submit"]'));
    const disabled = buttons.map(button => button.disabled);
    buttons.forEach(button => { button.disabled = true; button.classList.add('loading'); });
    message(group, '');
    let sent = false;
    try {
      const selection = selected(group);
      const { option, radios } = selection;
      const data = new FormData(form);
      const properties = {};
      for (const [key, value] of data.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (typeof File !== 'undefined' && value instanceof File && value.size) throw new Error('Los campos de archivo requieren una integración específica. No se añadió la compra.');
        if (match && typeof value === 'string') properties[match[1]] = value;
      }
      const main = mainItems(selection, properties);
      main.forEach(item => Object.assign(item.properties, {
        _floopy_bundle_program: group.dataset.program,
        _floopy_bundle_option: String(option),
        _floopy_bundle_size: String(selection.quantity)
      }));
      if (data.get('selling_plan')) throw new Error('Esta integración está preparada para compra única, no suscripciones.');
      let before = (await reconcile(await cart())).state;
      const gifts = [];
      const addedIds = new Set(main.map(item => item.id));
      for (const card of group.querySelectorAll('[data-fg-card]')) {
        if (!eligible(group, card, option) || card.dataset.available !== 'true' || !card.dataset.id) continue;
        if (addedIds.has(card.dataset.id)) throw new Error('Cada regalo debe usar una variante distinta del principal y de los otros regalos.');
        addedIds.add(card.dataset.id);
        if (before.items.some(item => owned(item) && item.properties._floopy_program === group.dataset.program && String(item.variant_id) === card.dataset.id)) continue;
        const allowedOptions = radios.map(radio => Number(radio.dataset.fgOption))
          .filter(number => permitted(group, card, number));
        gifts.push({ id: card.dataset.id, quantity: 1, properties: {
          _floopy_gift: 'v1', _floopy_program: group.dataset.program,
          _floopy_options: allowedOptions.join(',')
        } });
      }
      // Quantity bundles preserve their unit counts; variant bundles add one unit.
      // No automatic retry: an uncertain network result could otherwise duplicate a purchase.
      sent = true;
      const drawer = document.querySelector('cart-drawer');
      const sectionIds = drawer?.getSectionsToRender
        ? drawer.getSectionsToRender().map(section => section.section || section.id)
        : [];
      const payload = { items: [...main, ...gifts] };
      if (sectionIds.length) {
        payload.sections = sectionIds;
        payload.sections_url = window.location.pathname;
      }
      const added = await request('cart/add.js', payload);
      sessionStorage.setItem('floopy-gifts-active', '1');
      const checked = await reconcile(await cart());
      message(group, '');
      // A cleanup changes the cart: never reuse markup from before that cleanup.
      await renderCart(checked.state, checked.removed ? null : added.sections);
    } catch (error) {
      console.error('[Floopy regalos]', error);
      const text = sent
        ? `${error.message} No repitas la compra todavía: revisa el carrito. No se pudo confirmar toda la operación.`
        : error.message;
      message(group, text);
      announce(text);
      // If Shopify partially accepted an add, still try to remove charged gifts.
      if (sent) {
        try { await reconcile(await cart()); } catch (cleanupError) { console.error('[Floopy regalos] Verifica el carrito', cleanupError); }
      }
    } finally {
      buttons.forEach((button, index) => { button.disabled = disabled[index]; button.classList.remove('loading'); });
      busy = false;
      paint(group);
    }
  }
  // The original capture listener skips Floopy mode. Capture here only when gifts are configured.
  document.addEventListener('submit', event => {
    const form = event.target;
    const formId = form.getAttribute?.('id');
    const group = Array.from(document.querySelectorAll('[data-fg]')).find(node => node.dataset.form === formId);
    if (!group || !group.querySelector('[data-fg-card]')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    add(group, form);
  }, true);
  document.addEventListener('change', event => {
    if (event.target.closest('quantity-breaks') || event.target.matches('[name="id"]')) refresh();
  });
  async function guardCheckout() {
    if (checkoutBusy || busy || reconciling) return;
    checkoutBusy = true;
    try {
      const checked = await reconcile(await cart());
      if (checked.removed) {
        sessionStorage.setItem('floopy-gifts-notice', 'Retiramos regalos sin descuento gratuito o sin una variante válida. Revisa el carrito antes de continuar.');
        window.location.assign(url('cart'));
      } else window.location.assign(url('checkout'));
    } catch (error) { announce('No se pudo verificar el carrito. Intenta continuar nuevamente. ' + error.message); }
    finally { checkoutBusy = false; }
  }
  // Standard checkout only. Accelerated payment buttons must be disabled in the editor.
  document.addEventListener('click', event => {
    const button = event.target.closest('a[href],button[name="checkout"],input[name="checkout"]');
    if (!button) return;
    let checkoutLink = false;
    if (button.matches('a[href]')) {
      const target = new URL(button.href, window.location.origin);
      checkoutLink = target.origin === window.location.origin && /\/checkout\/?$/.test(target.pathname);
    }
    if (!checkoutLink && button.name !== 'checkout') return;
    if (!sessionStorage.getItem('floopy-gifts-active')) return;
    event.preventDefault(); event.stopImmediatePropagation(); guardCheckout();
  }, true);
  document.addEventListener('submit', event => {
    if (event.submitter?.name !== 'checkout') return;
    if (!sessionStorage.getItem('floopy-gifts-active')) return;
    event.preventDefault(); event.stopImmediatePropagation(); guardCheckout();
  }, true);
  async function init() {
    document.querySelectorAll('[data-fg]').forEach(paint);
    const notice = sessionStorage.getItem('floopy-gifts-notice');
    if (notice) { sessionStorage.removeItem('floopy-gifts-notice'); announce(notice); }
    if (!/\/cart\/?$/.test(window.location.pathname) || reconciling) return;
    reconciling = true;
    try {
      const result = await reconcile(await cart());
      if (result.state.items.some(owned)) sessionStorage.setItem('floopy-gifts-active', '1');
      if (result.removed) {
        sessionStorage.setItem('floopy-gifts-notice', 'Se retiraron regalos sin descuento gratuito o que ya no cumplían las condiciones.');
        window.location.reload();
      }
    } catch (error) { announce('No se pudieron verificar los regalos. ' + error.message); }
    finally { reconciling = false; }
  }
  function refresh() { document.querySelectorAll('[data-fg]').forEach(paint); }
  // No polling, global MutationObserver, or network requests on bundle selection.
  document.addEventListener('click', event => {
    if (event.target.closest('quantity-breaks')) requestAnimationFrame(refresh);
  });
  window.addEventListener('pageshow', refresh);
  window.FloopyGifts = { reconcile, eligible, owned, refresh };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', () => document.querySelectorAll('[data-fg]').forEach(paint));
})();


